import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import {
  Users,
  Building2,
  Package,
  User,
  CreditCard,
  Wallet,
  Shield,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  Zap,
  Lock,
  Globe,
  FileCheck,
  AlertTriangle,
  TrendingUp,
  Layers,
  Play,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocation } from "wouter";
import { MermaidDiagram } from "@/components/mermaid-diagram";
import { MarkdownReader } from "@/components/markdown-reader";
import { useAuth } from "@/lib/auth-context";

// Tab configuration with icons and colors (hierarchical)
interface DocTab {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: DocTab[];
  /** Roles that can see this tab. Omit for all roles. */
  roles?: string[];
}

const docsTabs: DocTab[] = [
  { id: "overview", label: "Overview", icon: Layers, roles: ["SUPER"] },
  { id: "video-demos", label: "Video Demos", icon: Play, roles: ["SUPER"] },
  {
    id: "use-cases",
    label: "Use Cases",
    icon: TrendingUp,
    roles: ["SUPER"],
    children: [
      { id: "use-cases.e2e-flows", label: "E2E Flows", icon: ArrowRight },
      { id: "use-cases.b2b", label: "B2B: Corporate", icon: Building2 },
      { id: "use-cases.b2c", label: "B2C: Consumer", icon: User },
      { id: "use-cases.comparison", label: "Comparison", icon: Layers },
    ]
  },
  {
    id: "compliance",
    label: "Compliance & Legal",
    icon: FileCheck,
    roles: ["SUPER"],
    children: [
      { id: "compliance.overview", label: "Overview", icon: Layers },
      { id: "compliance.executive", label: "Executive Summary", icon: Users },
      { id: "compliance.roadmap", label: "Roadmap", icon: TrendingUp },
      { id: "compliance.technical", label: "Technical", icon: Building2 },
      { id: "compliance.legal", label: "Legal", icon: Shield },
    ]
  },
  { id: "organizations", label: "Organizations", icon: Users, roles: ["SUPER"] },
  { id: "business-units", label: "Business Units", icon: Building2, roles: ["SUPER"] },
  { id: "products", label: "Products", icon: Package },
  { id: "customers", label: "Customers", icon: User },
  { id: "wallets", label: "Wallets", icon: Wallet },
  { id: "cards", label: "Cards", icon: CreditCard },
];

interface HierarchyNodeProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  badge?: string;
  badgeVariant?: "default" | "secondary" | "outline";
  color: string;
  children?: React.ReactNode;
}

function HierarchyNode({
  icon: Icon,
  title,
  description,
  badge,
  badgeVariant = "secondary",
  color,
  children,
}: HierarchyNodeProps) {
  return (
    <div className="relative">
      <div className="flex items-start gap-4">
        <div
          className={cn(
            "flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center shadow-lg",
            color
          )}
        >
          <Icon className="h-7 w-7 text-white" />
        </div>
        <div className="flex-1 pt-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">{title}</h3>
            {badge && (
              <Badge variant={badgeVariant} className="text-xs">
                {badge}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>
      {children && (
        <div className="ml-7 mt-4 pl-7 border-l-2 border-dashed border-muted-foreground/30">
          {children}
        </div>
      )}
    </div>
  );
}

function RelationshipCard({
  from,
  to,
  relationship,
}: {
  from: string;
  to: string;
  relationship: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="font-medium">{from}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
      <span className="text-muted-foreground">{relationship}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
      <span className="font-medium">{to}</span>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status, label }: { status: "active" | "pending" | "inactive"; label: string }) {
  const config = {
    active: { icon: CheckCircle2, color: "text-emerald-500 bg-emerald-500/10" },
    pending: { icon: Clock, color: "text-amber-500 bg-amber-500/10" },
    inactive: { icon: XCircle, color: "text-slate-400 bg-slate-400/10" },
  };
  const { icon: Icon, color } = config[status];
  return (
    <div className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium", color)}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
  );
}

// Feature Card Component
function FeatureCard({
  icon: Icon,
  title,
  description,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  color: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-xl border bg-card p-6 transition-all hover:shadow-md hover:border-primary/20">
      <div className={cn("inline-flex p-3 rounded-lg mb-4", color)}>
        <Icon className="h-5 w-5" />
      </div>
      <h4 className="font-semibold mb-1">{title}</h4>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

// Section Header Component
function SectionHeader({
  icon: Icon,
  title,
  description,
  color,
  badge,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  color: string;
  badge?: string;
}) {
  return (
    <div className="flex items-start gap-4 mb-8">
      <div className={cn("p-4 rounded-2xl shadow-lg", color)}>
        <Icon className="h-8 w-8 text-white" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
          {badge && <Badge variant="secondary">{badge}</Badge>}
        </div>
        <p className="text-muted-foreground mt-1">{description}</p>
      </div>
    </div>
  );
}

// Lifecycle Stage Component
function LifecycleStage({
  stages,
}: {
  stages: { label: string; description: string; status: "complete" | "current" | "upcoming" }[];
}) {
  return (
    <div className="relative">
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
      <div className="space-y-6">
        {stages.map((stage, idx) => (
          <div key={idx} className="relative flex gap-4">
            <div
              className={cn(
                "relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2",
                stage.status === "complete" && "bg-emerald-500 border-emerald-500 text-white",
                stage.status === "current" && "bg-primary border-primary text-white",
                stage.status === "upcoming" && "bg-background border-muted-foreground/30"
              )}
            >
              {stage.status === "complete" ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <span className="text-xs font-semibold">{idx + 1}</span>
              )}
            </div>
            <div className="flex-1 pt-1">
              <p className="font-medium">{stage.label}</p>
              <p className="text-sm text-muted-foreground">{stage.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Overview Tab Content
function OverviewTab() {
  const [hierarchyMode, setHierarchyMode] = useState<"b2b" | "b2c">("b2c");

  return (
    <div className="space-y-8">
      {/* Main Hierarchy Visual */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Entity Hierarchy
              </CardTitle>
              <CardDescription>
                {hierarchyMode === "b2c"
                  ? "B2C: Consumer financial products structure"
                  : "B2B: Corporate expense management structure"}
              </CardDescription>
            </div>
            {/* Toggle Button */}
            <div className="flex items-center gap-2 rounded-lg bg-muted p-1">
              <button
                onClick={() => setHierarchyMode("b2c")}
                className={cn(
                  "px-4 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  hierarchyMode === "b2c"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  B2C
                </div>
              </button>
              <button
                onClick={() => setHierarchyMode("b2b")}
                className={cn(
                  "px-4 py-2 rounded-md text-sm font-medium transition-all duration-200",
                  hierarchyMode === "b2b"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  B2B
                </div>
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {hierarchyMode === "b2c" ? (
            // B2C Hierarchy
            <HierarchyNode
              icon={Shield}
              title="VedaLogic (Super Admin)"
              description="Platform operator with full system access and oversight"
              badge="Root"
              badgeVariant="default"
              color="bg-gradient-to-br from-amber-500 to-orange-600"
            >
              <div className="space-y-6 py-2">
                <HierarchyNode
                  icon={Users}
                  title="Organization"
                  description="Financial institutions, banks, or large organizations"
                  badge="Level 1"
                  color="bg-gradient-to-br from-violet-500 to-purple-600"
                >
                  <div className="space-y-6 py-2">
                    <HierarchyNode
                      icon={Building2}
                      title="Business Unit"
                      description="Companies or business units under an Organization"
                      badge="Level 2"
                      color="bg-gradient-to-br from-blue-500 to-cyan-600"
                    >
                      <div className="space-y-6 py-2">
                        <HierarchyNode
                          icon={Package}
                          title="Product"
                          description="Card product configurations (Basic Card, Gold Card, etc.)"
                          badge="Level 3"
                          color="bg-gradient-to-br from-emerald-500 to-teal-600"
                        >
                          <div className="space-y-6 py-2">
                            <HierarchyNode
                              icon={User}
                              title="Customer"
                              description="End users who receive and use prepaid cards"
                              badge="Level 4"
                              color="bg-gradient-to-br from-rose-500 to-pink-600"
                            >
                              <div className="space-y-6 py-2">
                                <HierarchyNode
                                  icon={Wallet}
                                  title="Wallet"
                                  description="Funding source that holds customer balance"
                                  badge="Level 5"
                                  color="bg-gradient-to-br from-yellow-500 to-amber-600"
                                >
                                  <div className="space-y-6 py-2">
                                    <HierarchyNode
                                      icon={CreditCard}
                                      title="Card"
                                      description="Virtual card for personal spending"
                                      badge="Level 6"
                                      color="bg-gradient-to-br from-slate-600 to-slate-800"
                                    />
                                  </div>
                                </HierarchyNode>
                              </div>
                            </HierarchyNode>
                          </div>
                        </HierarchyNode>
                      </div>
                    </HierarchyNode>
                  </div>
                </HierarchyNode>
              </div>
            </HierarchyNode>
          ) : (
            // B2B Hierarchy
            <HierarchyNode
              icon={Shield}
              title="VedaLogic (Super Admin)"
              description="Platform operator with full system access and oversight"
              badge="Root"
              badgeVariant="default"
              color="bg-gradient-to-br from-amber-500 to-orange-600"
            >
              <div className="space-y-6 py-2">
                <HierarchyNode
                  icon={Users}
                  title="Organization"
                  description="Corporate entities managing employee expense programs"
                  badge="Level 1"
                  color="bg-gradient-to-br from-violet-500 to-purple-600"
                >
                  <div className="space-y-6 py-2">
                    <HierarchyNode
                      icon={Building2}
                      title="Business Unit"
                      description="Departments or teams (Marketing, Engineering, Sales)"
                      badge="Level 2"
                      color="bg-gradient-to-br from-blue-500 to-cyan-600"
                    >
                      <div className="space-y-6 py-2">
                        <HierarchyNode
                          icon={Users}
                          title="Staff Users"
                          description="Employees with admin roles who manage corporate cards"
                          badge="Level 3"
                          color="bg-gradient-to-br from-purple-500 to-indigo-600"
                        >
                          <div className="space-y-6 py-2">
                            <HierarchyNode
                              icon={CreditCard}
                              title="Virtual Cards"
                              description="Corporate cards for business expenses (Ad Spend, Travel, SaaS)"
                              badge="Level 4"
                              color="bg-gradient-to-br from-slate-600 to-slate-800"
                            />
                          </div>
                        </HierarchyNode>
                      </div>
                    </HierarchyNode>
                  </div>
                </HierarchyNode>
              </div>
            </HierarchyNode>
          )}
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {hierarchyMode === "b2c" ? "Ownership Chain (B2C)" : "Ownership Chain (B2B)"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {hierarchyMode === "b2c" ? (
                <>
                  <RelationshipCard from="Organization" to="Business Unit" relationship="owns" />
                  <RelationshipCard from="Business Unit" to="Product" relationship="offers" />
                  <RelationshipCard from="Product" to="Customer" relationship="enrolls" />
                  <RelationshipCard from="Customer" to="Wallet" relationship="holds" />
                  <RelationshipCard from="Wallet" to="Card" relationship="funds" />
                </>
              ) : (
                <>
                  <RelationshipCard from="Organization" to="Business Unit" relationship="owns" />
                  <RelationshipCard from="Business Unit" to="Staff Users" relationship="employs" />
                  <RelationshipCard from="Staff Users" to="Virtual Cards" relationship="manage" />
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {hierarchyMode === "b2c" ? "Typical Scale (B2C)" : "Typical Scale (B2B)"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              {hierarchyMode === "b2c"
                ? [
                    { from: "1 Organization", to: "can have many Business Units" },
                    { from: "1 Business Unit", to: "can have many Products" },
                    { from: "1 Product", to: "can have many Customers" },
                    { from: "1 Customer", to: "has 1 Wallet" },
                    { from: "1 Wallet", to: "can have many Cards" },
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between items-center">
                      <span className="text-muted-foreground">{item.from}</span>
                      <span className="font-medium">{item.to}</span>
                    </div>
                  ))
                : [
                    { from: "1 Organization", to: "can have many Business Units" },
                    { from: "1 Business Unit", to: "can have many Staff Users" },
                    { from: "1 Staff User", to: "can have many Virtual Cards" },
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between items-center">
                      <span className="text-muted-foreground">{item.from}</span>
                      <span className="font-medium">{item.to}</span>
                    </div>
                  ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Page Access Control Matrix — Super Admin only (this entire tab is already SUPER-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            Page Access Levels
          </CardTitle>
          <CardDescription>
            Which admin roles can access each page. Enforced by both sidebar visibility and server-side route guards.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Legend */}
            <div className="flex flex-wrap gap-3 text-xs">
              {[
                { label: "Super Admin", abbr: "S", color: "bg-red-500/10 text-red-600 border-red-500/20" },
                { label: "Organization", abbr: "O", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
                { label: "Business Unit", abbr: "B", color: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
                { label: "Finance", abbr: "F", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
              ].map((r) => (
                <div key={r.abbr} className="flex items-center gap-1.5">
                  <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 font-mono", r.color)}>{r.abbr}</Badge>
                  <span className="text-muted-foreground">{r.label}</span>
                </div>
              ))}
            </div>

            {/* Access table */}
            <div className="border rounded-lg overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-[1fr_60px_60px_60px_60px] bg-muted/50 px-4 py-2.5 text-xs font-medium text-muted-foreground border-b">
                <span>Page</span>
                <span className="text-center">S</span>
                <span className="text-center">O</span>
                <span className="text-center">B</span>
                <span className="text-center">F</span>
              </div>
              {/* Rows */}
              {[
                { section: "Core" },
                { page: "Dashboard", roles: ["S", "O", "B", "F"] },
                { page: "Settings", roles: ["S", "O", "B", "F"] },
                { page: "Reports", roles: ["S", "O", "B", "F"] },
                { page: "Transactions", roles: ["S", "O", "B", "F"] },
                { section: "Organization" },
                { page: "Organizations", roles: ["S"] },
                { page: "Business Units", roles: ["S", "O"] },
                { section: "Card Operations" },
                { page: "Products", roles: ["S", "O"] },
                { page: "Customers", roles: ["S", "O", "B"] },
                { page: "Wallets", roles: ["S", "O", "B"] },
                { page: "Cards", roles: ["S", "O", "B"] },
                { page: "Inventory", roles: ["S", "O"] },
                { section: "Operations" },
                { page: "Support", roles: ["S", "O", "B"] },
                { page: "Email Templates", roles: ["S"] },
                { section: "Developer" },
                { page: "API Keys", roles: ["S", "O"] },
                { page: "Webhooks", roles: ["S", "O"] },
                { page: "API Usage", roles: ["S", "O"] },
                { page: "Security Tests", roles: ["S", "O"] },
                { page: "API Docs", roles: ["S", "O"] },
                { section: "Platform" },
                { page: "Integrations", roles: ["S"] },
                { page: "Platform Analytics", roles: ["S"] },
                { page: "Audit Log", roles: ["S"] },
                { page: "Docs (filtered)", roles: ["S", "O"], note: "Org admins see Products, Customers, Wallets, Cards only" },
                { page: "Changelog", roles: ["S"] },
              ].map((row, i) => {
                if ("section" in row && row.section) {
                  return (
                    <div key={i} className="px-4 py-2 bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b">
                      {row.section}
                    </div>
                  );
                }
                const r = row as { page: string; roles: string[]; note?: string };
                const roleColors: Record<string, string> = {
                  S: "bg-red-500/10 text-red-600 border-red-500/20",
                  O: "bg-blue-500/10 text-blue-600 border-blue-500/20",
                  B: "bg-amber-500/10 text-amber-600 border-amber-500/20",
                  F: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
                };
                return (
                  <div key={i} className="grid grid-cols-[1fr_60px_60px_60px_60px] px-4 py-2 border-b last:border-b-0 hover:bg-muted/20 transition-colors">
                    <div>
                      <span className="text-sm">{r.page}</span>
                      {r.note && <span className="text-[10px] text-muted-foreground ml-2">({r.note})</span>}
                    </div>
                    {["S", "O", "B", "F"].map((role) => (
                      <div key={role} className="flex justify-center items-center">
                        {r.roles.includes(role) ? (
                          <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 font-mono", roleColors[role])}>{role}</Badge>
                        ) : (
                          <span className="text-muted-foreground/30 text-xs">—</span>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Organizations Tab
function OrganizationsTab() {
  return (
    <div className="space-y-8">
      <SectionHeader
        icon={Users}
        title="Organizations"
        description="Financial institutions and organizations that operate card programs"
        color="bg-gradient-to-br from-violet-500 to-purple-600"
        badge="Level 1"
      />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <FeatureCard
          icon={Globe}
          title="BIN Management"
          description="Owns and manages Bank Identification Numbers for card issuance"
          color="bg-violet-500/10 text-violet-600"
        />
        <FeatureCard
          icon={Lock}
          title="Compliance Policies"
          description="Sets regulatory and compliance requirements for all entities below"
          color="bg-violet-500/10 text-violet-600"
        />
        <FeatureCard
          icon={Building2}
          title="Admin Oversight"
          description="Manages multiple Business Units and their operations"
          color="bg-violet-500/10 text-violet-600"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Status Lifecycle</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <StatusBadge status="pending" label="Pending Approval" />
            <ArrowRight className="h-5 w-5 text-muted-foreground self-center" />
            <StatusBadge status="active" label="Active" />
            <ArrowRight className="h-5 w-5 text-muted-foreground self-center" />
            <StatusBadge status="inactive" label="Suspended" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Key Responsibilities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              "Approve Business Unit applications",
              "Monitor transaction volumes",
              "Set program-wide limits",
              "Manage fee structures",
              "Handle escalated support",
              "Generate compliance reports",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Business Units Tab
function BusinessUnitsTab() {
  return (
    <div className="space-y-8">
      <SectionHeader
        icon={Building2}
        title="Business Units"
        description="Business entities that manage card products and customer relationships"
        color="bg-gradient-to-br from-blue-500 to-cyan-600"
        badge="Level 2"
      />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <FeatureCard
          icon={Package}
          title="Product Creation"
          description="Design and configure card products for different use cases"
          color="bg-blue-500/10 text-blue-600"
        />
        <FeatureCard
          icon={User}
          title="Customer Management"
          description="Handle customer onboarding, KYC, and lifecycle management"
          color="bg-blue-500/10 text-blue-600"
        />
        <FeatureCard
          icon={TrendingUp}
          title="Operations"
          description="Day-to-day card operations and transaction monitoring"
          color="bg-blue-500/10 text-blue-600"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Onboarding Process</CardTitle>
        </CardHeader>
        <CardContent>
          <LifecycleStage
            stages={[
              { label: "Application Submitted", description: "Business details and documentation provided", status: "complete" },
              { label: "KYB Verification", description: "Know Your Business checks performed", status: "complete" },
              { label: "Organization Review", description: "Approval from parent organization", status: "current" },
              { label: "Account Activation", description: "Access granted and products can be created", status: "upcoming" },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// Products Tab
function ProductsTab() {
  return (
    <div className="space-y-8">
      <SectionHeader
        icon={Package}
        title="Products"
        description="Card program templates that define rules, limits, and features"
        color="bg-gradient-to-br from-emerald-500 to-teal-600"
        badge="Level 3"
      />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <FeatureCard
          icon={CreditCard}
          title="Card Configuration"
          description="Virtual card options with custom designs"
          color="bg-emerald-500/10 text-emerald-600"
        />
        <FeatureCard
          icon={Zap}
          title="Transaction Limits"
          description="Daily, monthly, and per-transaction spending limits"
          color="bg-emerald-500/10 text-emerald-600"
        />
        <FeatureCard
          icon={FileCheck}
          title="Fee Structures"
          description="Issuance fees, monthly fees, and transaction fees"
          color="bg-emerald-500/10 text-emerald-600"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Product Types</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              { name: "Travel Card", description: "Multi-currency support for travelers", icon: Globe },
              { name: "Gift Card", description: "Preloaded cards for gifting", icon: CreditCard },
              { name: "Payroll Card", description: "Employee salary disbursement", icon: Users },
            ].map((type, i) => (
              <div key={i} className="p-4 rounded-lg border bg-card">
                <type.icon className="h-8 w-8 text-emerald-500 mb-3" />
                <h4 className="font-semibold">{type.name}</h4>
                <p className="text-sm text-muted-foreground mt-1">{type.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Customers Tab
function CustomersTab() {
  return (
    <div className="space-y-8">
      <SectionHeader
        icon={User}
        title="Customers"
        description="End users who receive and use prepaid cards"
        color="bg-gradient-to-br from-rose-500 to-pink-600"
        badge="Level 4"
      />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <FeatureCard
          icon={FileCheck}
          title="KYC Verification"
          description="Identity verification for regulatory compliance"
          color="bg-rose-500/10 text-rose-600"
        />
        <FeatureCard
          icon={CreditCard}
          title="Card Issuance"
          description="Virtual cards issued upon approval"
          color="bg-rose-500/10 text-rose-600"
        />
        <FeatureCard
          icon={Wallet}
          title="Fund Management"
          description="Load, spend, and transfer funds"
          color="bg-rose-500/10 text-rose-600"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">KYC Status Levels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              { level: "Pending", description: "Documents submitted, awaiting review", color: "bg-amber-500/10 border-amber-500/20", icon: Clock },
              { level: "Verified", description: "Identity confirmed, full access granted", color: "bg-emerald-500/10 border-emerald-500/20", icon: CheckCircle2 },
              { level: "Failed", description: "Verification failed, action required", color: "bg-red-500/10 border-red-500/20", icon: AlertTriangle },
            ].map((kyc, i) => (
              <div key={i} className={cn("p-4 rounded-lg border", kyc.color)}>
                <kyc.icon className="h-6 w-6 mb-2" />
                <h4 className="font-semibold">{kyc.level}</h4>
                <p className="text-sm text-muted-foreground mt-1">{kyc.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Cards Tab
function CardsTab() {
  return (
    <div className="space-y-8">
      <SectionHeader
        icon={CreditCard}
        title="Cards"
        description="Virtual access instruments that spend from a wallet"
        color="bg-gradient-to-br from-slate-600 to-slate-800"
        badge="Level 6"
      />

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Card Types</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-4 p-4 rounded-lg border">
              <div className="p-2 rounded-lg bg-violet-500/10">
                <Zap className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <h4 className="font-semibold">Virtual Card</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Instant digital card for online and contactless purchases. No shipping required.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Card Status Flow</CardTitle>
          </CardHeader>
          <CardContent>
            <LifecycleStage
              stages={[
                { label: "Requested", description: "Card request submitted", status: "complete" },
                { label: "Issued", description: "Virtual card created", status: "complete" },
                { label: "Active", description: "Card ready for use", status: "current" },
                { label: "Blocked/Expired", description: "Card no longer usable", status: "upcoming" },
              ]}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Wallets Tab
function WalletsTab() {
  return (
    <div className="space-y-8">
      <SectionHeader
        icon={Wallet}
        title="Wallets"
        description="Funding sources that hold balance for card spending"
        color="bg-gradient-to-br from-yellow-500 to-amber-600"
        badge="Level 5"
      />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <FeatureCard
          icon={TrendingUp}
          title="Real-time Balance"
          description="Instant balance updates after every transaction"
          color="bg-amber-500/10 text-amber-600"
        />
        <FeatureCard
          icon={Globe}
          title="Multi-currency"
          description="Support for multiple currencies in a single wallet"
          color="bg-amber-500/10 text-amber-600"
        />
        <FeatureCard
          icon={Lock}
          title="Secure Funds"
          description="Protected by encryption and fraud monitoring"
          color="bg-amber-500/10 text-amber-600"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Transaction Types</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              { name: "Credit", description: "Funds loaded into wallet", positive: true },
              { name: "Debit", description: "Funds spent or withdrawn", positive: false },
              { name: "Transfer In", description: "Funds received from another wallet", positive: true },
              { name: "Transfer Out", description: "Funds sent to another wallet", positive: false },
              { name: "Refund", description: "Merchant refund credited", positive: true },
              { name: "Fee", description: "Service or transaction fee deducted", positive: false },
            ].map((tx, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="font-medium">{tx.name}</p>
                  <p className="text-xs text-muted-foreground">{tx.description}</p>
                </div>
                <Badge variant={tx.positive ? "default" : "secondary"}>
                  {tx.positive ? "+" : "-"}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Use Cases Tab - No longer needed as we render content directly
// Keeping this as a simple redirect/placeholder
function UseCasesTab() {
  return <B2BContent />;
}

// E2E Flows Content Component
function E2EFlowsContent() {
  return (
    <div className="space-y-8">
      <SectionHeader
        icon={ArrowRight}
        title="End-to-End Customer Flows"
        description="Complete customer journeys from onboarding through KYC verification and wallet funding"
        color="bg-gradient-to-br from-violet-500 to-indigo-600"
      />

      {/* Flow 1: KYC Verification */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-violet-500" />
            Flow 1: KYC Verification
          </CardTitle>
          <CardDescription>
            Customer completes identity verification via Signzy — document capture and face liveness check
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
              <Badge variant="outline" className="shrink-0">1</Badge>
              <div>
                <p className="font-semibold text-sm">Customer Login</p>
                <p className="text-xs text-muted-foreground">OTP-based phone authentication</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
              <Badge variant="outline" className="shrink-0">2</Badge>
              <div>
                <p className="font-semibold text-sm">Start KYC</p>
                <p className="text-xs text-muted-foreground">Signzy journey created via API</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
              <Badge variant="outline" className="shrink-0">3</Badge>
              <div>
                <p className="font-semibold text-sm">Verified</p>
                <p className="text-xs text-muted-foreground">Card issuance unlocked</p>
              </div>
            </div>
          </div>

          <MermaidDiagram
            modalOnly
            buttonLabel="Show KYC Verification Flow"
            chart={`flowchart TD
    LOGIN[Customer Login via OTP]
    ACCOUNT[Account Settings Page]
    START_KYC[Start KYC]
    JOURNEY[Signzy Journey Created]
    IFRAME[KYC Opens in Dialog]
    DOCS[Document Capture - Front and Back]
    LIVENESS[Face Liveness Check]
    VERIFIED[KYC Status: VERIFIED]
    ELIGIBLE[Card Issuance Unlocked]

    LOGIN --> ACCOUNT --> START_KYC
    START_KYC --> JOURNEY --> IFRAME
    IFRAME --> DOCS --> LIVENESS --> VERIFIED --> ELIGIBLE

    classDef portal fill:#e0e7ff,stroke:#4338ca,stroke-width:2px
    classDef api fill:#f0fdf4,stroke:#15803d,stroke-width:2px
    classDef signzy fill:#fef3c7,stroke:#b45309,stroke-width:2px
    classDef result fill:#dcfce7,stroke:#166534,stroke-width:3px

    class LOGIN,ACCOUNT portal
    class START_KYC,JOURNEY,IFRAME api
    class DOCS,LIVENESS signzy
    class VERIFIED,ELIGIBLE result`}
          />
        </CardContent>
      </Card>

      {/* Flow 2: QR Code Wallet Top-Up */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-emerald-500" />
            Flow 2: QR Code Wallet Top-Up
          </CardTitle>
          <CardDescription>
            Customer adds money to their wallet by scanning a UPI QR code with any payment app
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
              <Badge variant="outline" className="shrink-0">1</Badge>
              <div>
                <p className="font-semibold text-sm">Choose Amount</p>
                <p className="text-xs text-muted-foreground">Quick select or custom</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
              <Badge variant="outline" className="shrink-0">2</Badge>
              <div>
                <p className="font-semibold text-sm">Scan QR</p>
                <p className="text-xs text-muted-foreground">Any UPI app</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
              <Badge variant="outline" className="shrink-0">3</Badge>
              <div>
                <p className="font-semibold text-sm">Pay</p>
                <p className="text-xs text-muted-foreground">GPay, PhonePe, Paytm</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
              <Badge variant="outline" className="shrink-0">4</Badge>
              <div>
                <p className="font-semibold text-sm">Wallet Credited</p>
                <p className="text-xs text-muted-foreground">Instant balance update</p>
              </div>
            </div>
          </div>

          <MermaidDiagram
            modalOnly
            buttonLabel="Show Wallet Top-Up Flow"
            chart={`flowchart TD
    WALLET[My Wallet Page]
    ADD[Click Add Money]
    AMOUNT[Select Amount]
    GENERATE[Generate QR Code]
    QR[QR Code Displayed]
    SCAN[Customer Scans QR]
    PAY[Pay via UPI App]
    WEBHOOK[Payment Confirmed]
    CREDIT[Wallet Credited]
    SUCCESS[Success - Balance Updated]
    EXPIRED[QR Expired - Retry]

    WALLET --> ADD --> AMOUNT --> GENERATE --> QR
    QR --> SCAN --> PAY --> WEBHOOK --> CREDIT --> SUCCESS
    QR -. 5-min timeout .-> EXPIRED

    classDef portal fill:#e0e7ff,stroke:#4338ca,stroke-width:2px
    classDef process fill:#f0fdf4,stroke:#15803d,stroke-width:2px
    classDef payment fill:#fef3c7,stroke:#b45309,stroke-width:2px
    classDef result fill:#dcfce7,stroke:#166534,stroke-width:3px
    classDef error fill:#fef2f2,stroke:#991b1b,stroke-width:2px

    class WALLET,ADD portal
    class AMOUNT,GENERATE,QR process
    class SCAN,PAY,WEBHOOK payment
    class CREDIT,SUCCESS result
    class EXPIRED error`}
          />
        </CardContent>
      </Card>

      {/* Flow 3: Combined E2E — Onboarding to First Transaction */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Complete Journey: Onboarding to First Card Transaction
          </CardTitle>
          <CardDescription>
            The full customer lifecycle — from first login through KYC, wallet funding, card issuance, and first spend
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MermaidDiagram
            modalOnly
            buttonLabel="Show Complete Journey"
            chart={`flowchart TD
    A1[Phone + OTP Login] --> A2[Profile Created] --> A3[Wallet Auto-Created]
    A3 --> P1([ONBOARDING COMPLETE])
    P1 --> B1[Start KYC] --> B2[Document Capture] --> B3[Face Liveness] --> B4[KYC Verified]
    B4 --> P2([KYC COMPLETE])
    P2 --> C1[Add Money] --> C2[Scan UPI QR] --> C3[Pay via UPI App] --> C4[Wallet Credited]
    C4 --> P3([WALLET FUNDED])
    P3 --> D1[Issue Virtual Card] --> D2[View Card Details] --> D3[Make Purchase] --> D4[Transaction Logged]

    classDef onboard fill:#e0e7ff,stroke:#4338ca,stroke-width:2px
    classDef kyc fill:#fef3c7,stroke:#b45309,stroke-width:2px
    classDef fund fill:#dcfce7,stroke:#166534,stroke-width:2px
    classDef card fill:#fce7f3,stroke:#9d174d,stroke-width:2px
    classDef phase fill:#f1f5f9,stroke:#64748b,stroke-width:2px

    class A1,A2,A3 onboard
    class B1,B2,B3,B4 kyc
    class C1,C2,C3,C4 fund
    class D1,D2,D3,D4 card
    class P1,P2,P3 phase`}
          />
        </CardContent>
      </Card>

      {/* Architecture: System Interaction */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            System Architecture: Service Interactions
          </CardTitle>
          <CardDescription>
            How the platform components interact during KYC and payment flows
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MermaidDiagram
            modalOnly
            buttonLabel="Show System Architecture"
            chart={`sequenceDiagram
    participant C as Customer
    participant P as Portal
    participant S as Server
    participant SZ as Signzy
    participant PH as PayHub
    participant U as UPI App

    Note over C,SZ: KYC Flow
    C->>P: Open Account Settings
    P->>S: Start KYC Journey
    S->>SZ: Create Journey
    SZ-->>S: Journey URL
    S-->>P: Journey URL
    P->>C: Show KYC Iframe
    C->>SZ: Documents + Liveness
    SZ-->>S: KYC Complete
    S-->>P: Status VERIFIED

    Note over C,U: Wallet Top-Up Flow
    C->>P: Click Add Money
    P->>S: Generate QR
    S->>PH: Payment Intent
    PH-->>S: QR Code
    S-->>P: QR Data
    P->>C: Display QR
    C->>U: Scan and Pay
    U->>PH: UPI Payment
    PH-->>S: Payment Success
    S-->>P: Wallet Credited
    P->>C: Success`}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// B2B Content Component
function B2BContent() {
  return (
    <div className="space-y-8">
      <SectionHeader
        icon={Building2}
        title="B2B: Corporate Expense Management"
        description="Organizations have business units with staff members who are issued virtual cards for business-related expenses"
        color="bg-gradient-to-br from-blue-500 to-cyan-600"
      />

      {/* Key Characteristics */}
      <Card>
        <CardHeader>
          <CardTitle>Key Characteristics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Target Users</p>
                <p className="text-sm text-muted-foreground">Corporate employees, finance teams, department managers</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Zap className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Primary Goal</p>
                <p className="text-sm text-muted-foreground">Controlled corporate spending with departmental oversight</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CreditCard className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Card Purpose</p>
                <p className="text-sm text-muted-foreground">Business expenses (travel, procurement, subscriptions)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Wallet className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Funding Model</p>
                <p className="text-sm text-muted-foreground">Company-funded cards with preset limits</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Hierarchy */}
      <Card>
        <CardHeader>
          <CardTitle>Business Hierarchy</CardTitle>
          <CardDescription>How organizations structure their corporate card programs</CardDescription>
        </CardHeader>
        <CardContent>
          <MermaidDiagram
            chart={`graph TD
    ORG["🏢 ORGANIZATION<br/>Acme Corp / TechStart Inc<br/><br/>⚙️ Settings:<br/>• Max Business Units<br/>• Platform Features<br/>• Branding"]

    BU1["📊 Business Unit<br/>Marketing Department"]
    BU2["💻 Business Unit<br/>Engineering Department"]
    BU3["📈 Business Unit<br/>Sales Department"]

    STAFF1["👥 Staff Users<br/>• Manager<br/>• Coordinator<br/>• Analyst"]
    STAFF2["👥 Staff Users<br/>• Tech Lead<br/>• Developer<br/>• Designer"]
    STAFF3["👥 Staff Users<br/>• Sales Rep<br/>• Manager<br/>• Associate"]

    CARDS1["💳 Virtual Cards<br/>• Ad Spend<br/>• Events<br/>• Content"]
    CARDS2["💳 Virtual Cards<br/>• AWS/Cloud<br/>• Software<br/>• Licenses"]
    CARDS3["💳 Virtual Cards<br/>• Travel<br/>• Client Meetings"]

    ORG --> BU1
    ORG --> BU2
    ORG --> BU3

    BU1 --> STAFF1
    BU2 --> STAFF2
    BU3 --> STAFF3

    STAFF1 --> CARDS1
    STAFF2 --> CARDS2
    STAFF3 --> CARDS3

    classDef orgStyle fill:#e1f5ff,stroke:#01579b,stroke-width:3px
    classDef buStyle fill:#fff3e0,stroke:#e65100,stroke-width:2px
    classDef staffStyle fill:#f3e5f5,stroke:#4a148c,stroke-width:2px
    classDef cardStyle fill:#e8f5e9,stroke:#1b5e20,stroke-width:2px

    class ORG orgStyle
    class BU1,BU2,BU3 buStyle
    class STAFF1,STAFF2,STAFF3 staffStyle
    class CARDS1,CARDS2,CARDS3 cardStyle`}
          />
        </CardContent>
      </Card>

      {/* Staff Card Issuance Workflow */}
      <Card>
        <CardHeader>
          <CardTitle>Staff Card Issuance Workflow</CardTitle>
          <CardDescription>How staff members request and receive corporate cards</CardDescription>
        </CardHeader>
        <CardContent>
          <MermaidDiagram
            chart={`sequenceDiagram
    participant Staff as Staff Member
    participant BUAdmin as Business Unit Admin
    participant Platform as Platform
    participant CardIssuer as Card Issuer API

    Staff->>BUAdmin: Request Virtual Card
    Note over Staff: Specify: Purpose, Limits, Duration
    BUAdmin->>BUAdmin: Review & Approve Request
    BUAdmin->>Platform: Approve Card Issuance
    Platform->>Platform: Validate Against Business Unit Limits
    Platform->>CardIssuer: Create Virtual Card
    CardIssuer-->>Platform: Card Details (Last 4, Expiry)
    Platform-->>Staff: Card Issued & Available
    Staff->>Staff: View Card in Dashboard`}
          />
        </CardContent>
      </Card>

      {/* Business Value */}
      <Card>
        <CardHeader>
          <CardTitle>Business Value</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            {[
              { title: "Centralized Control", desc: "Organization maintains oversight of all corporate spending" },
              { title: "Departmental Autonomy", desc: "Business units manage their own budgets and staff" },
              { title: "Real-Time Visibility", desc: "Instant transaction notifications and reporting" },
              { title: "Reduced Fraud", desc: "Individual card limits, MCC controls, instant freeze" },
              { title: "Simplified Reconciliation", desc: "Automated expense tracking and categorization" },
              { title: "Audit Compliance", desc: "Complete transaction history with actor tracking" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// B2C Content Component
function B2CContent() {
  return (
    <div className="space-y-8">
      <SectionHeader
        icon={User}
        title="B2C: Consumer Financial Products"
        description="Organizations create consumer-facing financial products through business units"
        color="bg-gradient-to-br from-rose-500 to-pink-600"
      />

      {/* Key Characteristics */}
      <Card>
        <CardHeader>
          <CardTitle>Key Characteristics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Target Users</p>
                <p className="text-sm text-muted-foreground">End consumers, retail customers</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Package className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Primary Goal</p>
                <p className="text-sm text-muted-foreground">Consumer financial services and payment products</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CreditCard className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Card Purpose</p>
                <p className="text-sm text-muted-foreground">Personal spending, P2P transfers, bill payments</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Wallet className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Funding Model</p>
                <p className="text-sm text-muted-foreground">Consumer-funded (bank account, P2P, paycheck)</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Hierarchy */}
      <Card>
        <CardHeader>
          <CardTitle>Business Hierarchy</CardTitle>
          <CardDescription>How organizations structure consumer card products</CardDescription>
        </CardHeader>
        <CardContent>
          <MermaidDiagram
            chart={`graph TD
    ORG["🏢 ORGANIZATION<br/>NeoBank / PayWallet Inc<br/><br/>⚙️ Settings:<br/>• Max Customers<br/>• Features<br/>• Branding"]

    BU1["📊 Business Unit<br/>Retail Banking"]
    BU2["💎 Business Unit<br/>Premium Rewards"]

    PROD1["📦 Product<br/>Basic Card<br/>No Fee"]
    PROD2["📦 Product<br/>Gold Card<br/>Cashback"]

    CUST1["👤 Customer A"]
    CUST2["👤 Customer B"]

    WALLET1["💰 Wallet AUD<br/>Balance: $500"]
    WALLET2["💰 Wallet AUD<br/>Balance: $1,200"]

    CARD1["💳 Cards<br/>• Main Card<br/>• Subscriptions"]
    CARD2["💳 Cards<br/>• Main Card"]

    ORG --> BU1
    ORG --> BU2

    BU1 --> PROD1
    BU2 --> PROD2

    PROD1 --> CUST1
    PROD2 --> CUST2

    CUST1 --> WALLET1
    CUST2 --> WALLET2

    WALLET1 --> CARD1
    WALLET2 --> CARD2

    classDef orgStyle fill:#e1f5ff,stroke:#01579b,stroke-width:3px
    classDef buStyle fill:#fff3e0,stroke:#e65100,stroke-width:2px
    classDef prodStyle fill:#fce4ec,stroke:#880e4f,stroke-width:2px
    classDef custStyle fill:#f3e5f5,stroke:#4a148c,stroke-width:2px
    classDef walletStyle fill:#fff9c4,stroke:#f57f17,stroke-width:2px
    classDef cardStyle fill:#e8f5e9,stroke:#1b5e20,stroke-width:2px

    class ORG orgStyle
    class BU1,BU2 buStyle
    class PROD1,PROD2 prodStyle
    class CUST1,CUST2 custStyle
    class WALLET1,WALLET2 walletStyle
    class CARD1,CARD2 cardStyle`}
          />
        </CardContent>
      </Card>

      {/* Customer Onboarding Journey */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Onboarding Journey</CardTitle>
          <CardDescription>From signup to first transaction</CardDescription>
        </CardHeader>
        <CardContent>
          <MermaidDiagram
            chart={`sequenceDiagram
    participant Customer
    participant Portal as Customer Portal
    participant Platform
    participant KYC as KYC Provider
    participant CardIssuer

    Customer->>Portal: Sign Up (Phone/Email)
    Portal->>Customer: Send OTP
    Customer->>Portal: Verify OTP
    Portal->>Platform: Create Customer Account
    Platform->>Customer: Account Created (DRAFT status)

    Customer->>Portal: Complete Profile (Name, DOB, Address)
    Portal->>Platform: Update Customer Info

    Customer->>Portal: Start KYC Process
    Portal->>KYC: Submit Documents (Aadhaar/Passport)
    KYC->>KYC: Verify Identity
    KYC-->>Platform: KYC Verified
    Platform->>Platform: Update Customer Status → ACTIVE

    Platform->>Platform: Create Default Wallet
    Platform->>CardIssuer: Request Virtual Card
    CardIssuer-->>Platform: Card Issued
    Platform->>Customer: Welcome! Your card is ready

    Note over Customer: Customer Can Now<br/>Load Funds & Make Purchases`}
          />
        </CardContent>
      </Card>

      {/* Customer Lifecycle */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Lifecycle</CardTitle>
          <CardDescription>States from prospect to active user or churn</CardDescription>
        </CardHeader>
        <CardContent>
          <MermaidDiagram
            chart={`stateDiagram-v2
    [*] --> Prospect: Customer Discovers Product
    Prospect --> Signup: Initiates Sign Up
    Signup --> KYC_Pending: Basic Info Submitted
    KYC_Pending --> KYC_InProgress: Starts KYC Process
    KYC_InProgress --> KYC_Verified: Documents Verified
    KYC_InProgress --> KYC_Rejected: Verification Failed
    KYC_Rejected --> [*]: Account Rejected

    KYC_Verified --> Active: Account Activated
    Active --> Wallet_Created: Default Wallet Created
    Wallet_Created --> Card_Issued: First Card Issued
    Card_Issued --> Active_User: First Transaction

    Active_User --> Active_User: Regular Usage
    Active_User --> Inactive: No Activity 90+ Days
    Inactive --> Active_User: Re-engagement
    Inactive --> Churned: No Activity 180+ Days

    Active_User --> Suspended: Suspicious Activity
    Suspended --> Active_User: Investigation Resolved
    Suspended --> Closed: Confirmed Fraud

    Closed --> [*]
    Churned --> [*]`}
          />
        </CardContent>
      </Card>

      {/* Business Value */}
      <Card>
        <CardHeader>
          <CardTitle>Business Value</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            {[
              { title: "Scalable Product Offering", desc: "Create multiple card products for different customer segments" },
              { title: "Customer Acquisition", desc: "Frictionless onboarding with digital KYC" },
              { title: "Revenue Streams", desc: "Interchange fees, wallet load fees, premium subscriptions" },
              { title: "Customer Engagement", desc: "Self-service portal, real-time notifications" },
              { title: "Regulatory Compliance", desc: "Built-in KYC/AML workflows, transaction monitoring" },
              { title: "Data Insights", desc: "Customer spending patterns, product performance analytics" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Comparison Content Component
function ComparisonContent() {
  return (
    <div className="space-y-8">
      <SectionHeader
        icon={Layers}
        title="B2B vs B2C Comparison"
        description="Key differences between corporate expense and consumer product models"
        color="bg-gradient-to-br from-violet-500 to-purple-600"
      />

      {/* Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Side-by-Side Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-semibold">Aspect</th>
                  <th className="text-left p-3 font-semibold bg-blue-50 dark:bg-blue-950/20">B2B (Corporate)</th>
                  <th className="text-left p-3 font-semibold bg-rose-50 dark:bg-rose-950/20">B2C (Consumer)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { aspect: "Primary User", b2b: "Corporate employees", b2c: "Retail consumers" },
                  { aspect: "Card Holder", b2b: "Staff member (Admin with BUSINESS_UNIT role)", b2c: "Customer" },
                  { aspect: "Funding Source", b2b: "Company funds", b2c: "Customer funds (self-load)" },
                  { aspect: "Card Purpose", b2b: "Business expenses", b2c: "Personal spending" },
                  { aspect: "Approval Workflow", b2b: "Business Unit Admin approves", b2c: "Automatic upon KYC completion" },
                  { aspect: "Spending Controls", b2b: "Centrally managed by BU Admin", b2c: "Self-managed by customer" },
                  { aspect: "Transaction Visibility", b2b: "Organization, BU Admin, Finance Team", b2c: "Customer only" },
                  { aspect: "KYC Requirements", b2b: "Internal employee verification", b2c: "Regulatory KYC (Aadhaar, Passport)" },
                  { aspect: "Product Concept", b2b: "Not applicable (cards per business unit)", b2c: "Multiple products with features/limits" },
                  { aspect: "Wallet Model", b2b: "Implicit (business unit budget)", b2c: "Explicit (customer wallet with balance)" },
                ].map((row, i) => (
                  <tr key={i} className="border-b last:border-b-0">
                    <td className="p-3 font-medium">{row.aspect}</td>
                    <td className="p-3 bg-blue-50/50 dark:bg-blue-950/10">{row.b2b}</td>
                    <td className="p-3 bg-rose-50/50 dark:bg-rose-950/10">{row.b2c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Technical Architecture Comparison */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-500" />
              B2B Data Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MermaidDiagram
              chart={`graph LR
    ORG[Organization] --> BU[Business Unit:<br/>Marketing]

    BU --> ADMIN1[Admin: Manager<br/>role: BUSINESS_UNIT]
    BU --> ADMIN2[Admin: Coordinator<br/>role: BUSINESS_UNIT]

    ADMIN1 --> CARD1[Virtual Card<br/>Ad Spend]
    ADMIN2 --> CARD2[Virtual Card<br/>Events]

    classDef orgClass fill:#e1f5ff,stroke:#01579b
    classDef buClass fill:#fff3e0,stroke:#e65100
    classDef adminClass fill:#f3e5f5,stroke:#4a148c
    classDef cardClass fill:#e8f5e9,stroke:#1b5e20

    class ORG orgClass
    class BU buClass
    class ADMIN1,ADMIN2 adminClass
    class CARD1,CARD2 cardClass`}
            />
            <div className="mt-4 space-y-2 text-sm">
              <p className="font-semibold">Schema Tables:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>organizations</li>
                <li>business_units</li>
                <li>admins (role: BUSINESS_UNIT)</li>
                <li>virtual_cards</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-rose-500" />
              B2C Data Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MermaidDiagram
              chart={`graph LR
    ORG[Organization] --> BU[Business Unit:<br/>Retail]

    BU --> PROD[Product:<br/>Basic Card]

    PROD --> CUST[Customer A]

    CUST --> WALLET[Wallet AUD<br/>$500]

    WALLET --> CARD1[Virtual Card:<br/>Main]
    WALLET --> CARD2[Virtual Card:<br/>Subscriptions]

    classDef orgClass fill:#e1f5ff,stroke:#01579b
    classDef buClass fill:#fff3e0,stroke:#e65100
    classDef prodClass fill:#fce4ec,stroke:#880e4f
    classDef custClass fill:#f3e5f5,stroke:#4a148c
    classDef walletClass fill:#fff9c4,stroke:#f57f17
    classDef cardClass fill:#e8f5e9,stroke:#1b5e20

    class ORG orgClass
    class BU buClass
    class PROD prodClass
    class CUST custClass
    class WALLET walletClass
    class CARD1,CARD2 cardClass`}
            />
            <div className="mt-4 space-y-2 text-sm">
              <p className="font-semibold">Schema Tables:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>organizations</li>
                <li>business_units</li>
                <li>products</li>
                <li>customers</li>
                <li>wallets</li>
                <li>virtual_cards</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Compliance Overview Content
function ComplianceOverviewContent() {
  return (
    <div className="space-y-6">
      <SectionHeader
        icon={FileCheck}
        title="Compliance & Legal Documentation"
        description="Regulatory compliance roadmap for Indian fintech market (RBI, DPDP Act, AML/CFT, PCI-DSS)"
        color="bg-gradient-to-br from-amber-500 to-orange-600"
      />

      {/* Critical Deadlines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Critical Compliance Deadlines
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LifecycleStage
            stages={[
              {
                label: "April 1, 2026 - RBI Transaction 2FA",
                description: "Mandatory dynamic 2FA for all payment transactions",
                status: "upcoming",
              },
              {
                label: "June 30, 2026 - DPDP Foundation",
                description: "DPO appointment, consent management, data retention, breach notification",
                status: "upcoming",
              },
              {
                label: "September 30, 2026 - AML/CFT",
                description: "PEP/sanctions screening, STR filing, transaction monitoring",
                status: "upcoming",
              },
              {
                label: "December 31, 2026 - PCI-DSS & DPDP Full",
                description: "Card tokenization, data subject rights, compliance dashboard",
                status: "upcoming",
              },
            ]}
          />
        </CardContent>
      </Card>

      {/* Regulatory Frameworks */}
      <Card>
        <CardHeader>
          <CardTitle>Regulatory Frameworks Covered</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              icon={Shield}
              title="RBI Payment Systems"
              description="Transaction 2FA, UPI performance (<10s), cross-border controls"
              color="bg-amber-500/10 text-amber-600"
            />
            <FeatureCard
              icon={Lock}
              title="DPDP Act 2023"
              description="DPO, consent, data retention, breach notification, data subject rights"
              color="bg-blue-500/10 text-blue-600"
            />
            <FeatureCard
              icon={FileCheck}
              title="AML/CFT (PMLA)"
              description="PEP/sanctions screening, STR filing, beneficial ownership"
              color="bg-rose-500/10 text-rose-600"
            />
            <FeatureCard
              icon={CreditCard}
              title="PCI-DSS v4.0"
              description="Card tokenization, SAQ compliance, 12 requirements"
              color="bg-violet-500/10 text-violet-600"
            />
            <FeatureCard
              icon={Globe}
              title="FEMA"
              description="LRS limits ($250K), cross-border transaction controls"
              color="bg-emerald-500/10 text-emerald-600"
            />
            <FeatureCard
              icon={Zap}
              title="NPCI UPI Guidelines"
              description="10-second timeout, performance logging, monitoring"
              color="bg-yellow-500/10 text-yellow-600"
            />
          </div>
        </CardContent>
      </Card>

      {/* Budget Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Budget Overview (12 Months)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 rounded-lg border bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20">
              <p className="text-sm text-muted-foreground">Base Budget</p>
              <p className="text-2xl font-bold text-emerald-600">₹1.35 crore</p>
              <p className="text-xs text-muted-foreground mt-1">Conservative estimate</p>
            </div>
            <div className="p-4 rounded-lg border bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
              <p className="text-sm text-muted-foreground">With Contingency</p>
              <p className="text-2xl font-bold text-amber-600">₹1.53 crore</p>
              <p className="text-xs text-muted-foreground mt-1">+15% buffer</p>
            </div>
            <div className="p-4 rounded-lg border bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/20">
              <p className="text-sm text-muted-foreground">Full Scope</p>
              <p className="text-2xl font-bold text-violet-600">₹1.71 crore</p>
              <p className="text-xs text-muted-foreground mt-1">With external audits</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Compliance Document Content Component
function ComplianceDocContent({
  title,
  description,
  icon: Icon,
  filePath,
}: {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  filePath: string;
}) {
  return (
    <div className="space-y-6">
      <SectionHeader
        icon={Icon}
        title={title}
        description={description}
        color="bg-gradient-to-br from-amber-500 to-orange-600"
      />
      <Card>
        <CardContent className="pt-6">
          <MarkdownReader filePath={filePath} />
        </CardContent>
      </Card>
    </div>
  );
}

// Compliance Tab - Redirect to overview
function ComplianceTab() {
  return <ComplianceOverviewContent />;
}

// Video Demos Tab — video catalog with metadata
interface VideoEntry {
  filename: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  tags: string[];
  steps?: number;
}

const featuredVideos: VideoEntry[] = [
  {
    filename: "B2BMasterFlow.mp4",
    title: "Complete B2B Admin Walkthrough",
    description:
      "End-to-end admin platform demo — login, create organization, business unit, product, onboard customer, issue card, load wallet, view transactions, integrations, reports, and settings.",
    icon: Building2,
    iconColor: "text-indigo-500",
    tags: ["Login", "Organizations", "Business Units", "Products", "Customers", "Cards", "Wallets", "Transactions", "Integrations", "Reports", "Settings"],
    steps: 38,
  },
  {
    filename: "B2CMasterFlow.mp4",
    title: "Complete B2C Customer Portal",
    description:
      "Full customer self-service portal experience — login with OTP, home dashboard, wallet overview & top-up, card management, transaction history, account settings, and help center.",
    icon: User,
    iconColor: "text-pink-500",
    tags: ["Login & OTP", "Dashboard", "Wallet", "Top-Up", "Cards", "Transactions", "Account", "Help"],
    steps: 20,
  },
];

const adminVideos: VideoEntry[] = [
  {
    filename: "AdminLogin.mp4",
    title: "Login & Dashboard",
    description: "Admin sign-in flow and executive dashboard with KPI cards, operations metrics, and sidebar navigation.",
    icon: Lock,
    iconColor: "text-blue-500",
    tags: ["Authentication", "KPIs", "Dashboard"],
  },
  {
    filename: "OrgProducts.mp4",
    title: "Organizations & Products",
    description: "Browse organizations, business units, API keys, and card product configurations with spending limits.",
    icon: Package,
    iconColor: "text-emerald-500",
    tags: ["Organizations", "Business Units", "Products", "Limits"],
  },
  {
    filename: "CustomerOnboard.mp4",
    title: "Customer Management",
    description: "Customer profiles, KYC verification status, issued cards, wallet balances, and transaction history.",
    icon: Users,
    iconColor: "text-rose-500",
    tags: ["Customers", "KYC", "Cards", "Wallets"],
  },
  {
    filename: "Integrations.mp4",
    title: "Integration Partners",
    description: "Test PayHub, Stripe, and Signzy integrations — connection tests, balance checks, and KYC provider status.",
    icon: Zap,
    iconColor: "text-amber-500",
    tags: ["PayHub", "Stripe", "Signzy", "Testing"],
  },
  {
    filename: "Reports.mp4",
    title: "Reports & Analytics",
    description: "Business KPIs, date-range reporting, platform-wide analytics, and detailed audit log with filtering.",
    icon: TrendingUp,
    iconColor: "text-violet-500",
    tags: ["KPIs", "Analytics", "Audit Log"],
  },
  {
    filename: "Settings.mp4",
    title: "Settings & Team",
    description: "Admin profile, team member management with roles, feature flags, and theme configuration with dark mode.",
    icon: Globe,
    iconColor: "text-cyan-500",
    tags: ["Profile", "Team Roles", "Feature Flags", "Theme"],
  },
];

const portalVideos: VideoEntry[] = [
  {
    filename: "PortalLogin.mp4",
    title: "Portal Login & OTP",
    description: "Customer sign-in with phone number, OTP delivery, and 6-digit verification code entry.",
    icon: Lock,
    iconColor: "text-pink-500",
    tags: ["Phone Auth", "OTP", "Verification"],
  },
  {
    filename: "PortalWallet.mp4",
    title: "Wallet & Top-Up",
    description: "Wallet balance overview, spending statistics (credits, debits, net), quick top-up with preset amounts, and transaction history.",
    icon: Wallet,
    iconColor: "text-amber-500",
    tags: ["Balance", "Spending Stats", "Top-Up", "Transactions"],
  },
  {
    filename: "PortalCards.mp4",
    title: "Card Management",
    description: "Card portfolio grid with status badges, card detail view with number and expiry, spending limits with progress bars.",
    icon: CreditCard,
    iconColor: "text-slate-500",
    tags: ["Card Grid", "Card Details", "Limits", "Activity"],
  },
  {
    filename: "PortalFullJourney.mp4",
    title: "Full Portal Journey",
    description: "Complete walkthrough of every portal section — home, wallet, cards, transactions, account settings, and help center.",
    icon: Play,
    iconColor: "text-indigo-500",
    tags: ["Home", "Wallet", "Cards", "Transactions", "Account", "Help"],
  },
];

function VideoCard({
  entry,
  video,
  featured,
}: {
  entry: VideoEntry;
  video?: { filename: string; url: string; size: number };
  featured?: boolean;
}) {
  const formatSize = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  const Icon = entry.icon;

  return (
    <Card>
      <CardHeader className={featured ? undefined : "pb-3"}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={cn("flex items-center gap-2", !featured && "text-base")}>
              <Icon className={cn("h-5 w-5", entry.iconColor)} />
              {entry.title}
            </CardTitle>
            <CardDescription className="mt-1.5">{entry.description}</CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {video && (
              <Badge variant="outline" className="text-xs">
                {formatSize(video.size)}
              </Badge>
            )}
            {entry.steps && (
              <Badge className="bg-indigo-500/10 text-indigo-600 border-indigo-500/20">
                {entry.steps} Steps
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {video ? (
          <div className="rounded-xl overflow-hidden border bg-black aspect-video">
            <video controls className="w-full h-full" preload="metadata">
              <source src={video.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        ) : (
          <div className="rounded-xl border bg-muted/50 aspect-video flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Video not yet uploaded</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {entry.tags.map((tag) => (
            <Badge key={tag} variant="outline">{tag}</Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function VideoDemosTab() {
  const { data } = useQuery<{ videos: { filename: string; url: string; size: number }[] }>({
    queryKey: ["/api/upload/videos"],
  });

  const videos = data?.videos || [];
  const findVideo = (filename: string) => videos.find((v) => v.filename === filename);

  return (
    <div className="space-y-10">
      <SectionHeader
        icon={Play}
        title="Platform Video Demos"
        description="Watch guided walkthroughs of the VedaLogic Virtual Card platform"
        color="bg-gradient-to-br from-indigo-500 to-violet-600"
      />

      {/* Featured: Complete Walkthroughs */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Shield className="h-5 w-5 text-indigo-500" />
          Complete Platform Walkthroughs
        </h3>
        {featuredVideos.map((entry) => (
          <VideoCard key={entry.filename} entry={entry} video={findVideo(entry.filename)} featured />
        ))}
      </div>

      {/* Admin Platform */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Building2 className="h-5 w-5 text-blue-500" />
          Admin Dashboard Features
        </h3>
        <div className="grid gap-6 lg:grid-cols-2">
          {adminVideos.map((entry) => (
            <VideoCard key={entry.filename} entry={entry} video={findVideo(entry.filename)} />
          ))}
        </div>
      </div>

      {/* Customer Portal */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <User className="h-5 w-5 text-pink-500" />
          Customer Portal Features
        </h3>
        <div className="grid gap-6 lg:grid-cols-2">
          {portalVideos.map((entry) => (
            <VideoCard key={entry.filename} entry={entry} video={findVideo(entry.filename)} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Docs() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const role = user?.role;

  // Filter tabs by role — tabs without a roles array are visible to everyone
  const visibleTabs = docsTabs.filter((tab) => !tab.roles || tab.roles.includes(role || ""));
  const defaultTab = visibleTabs[0]?.id || "products";

  // Parse tab from URL query params
  const urlParams = new URLSearchParams(location.split("?")[1] || "");
  const requestedTab = urlParams.get("tab") || defaultTab;
  // If the requested tab isn't visible to this role, fall back to the first visible tab
  const allVisibleIds = visibleTabs.flatMap((t) => [t.id, ...(t.children?.map((c) => c.id) || [])]);
  const tabFromUrl = allVisibleIds.includes(requestedTab) ? requestedTab : defaultTab;
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [expandedTabs, setExpandedTabs] = useState<string[]>([]);

  // Auto-expand parent if child is active
  useEffect(() => {
    const parentId = tabFromUrl.split(".")[0];
    if (tabFromUrl.includes(".") && !expandedTabs.includes(parentId)) {
      setExpandedTabs([parentId]);
    }
  }, [tabFromUrl]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setLocation(`/docs?tab=${value}`);
  };

  const toggleExpand = (tabId: string) => {
    setExpandedTabs((prev) =>
      prev.includes(tabId) ? prev.filter((id) => id !== tabId) : [...prev, tabId]
    );
  };

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Sidebar Navigation */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex w-full">
        <div className="w-64 border-r bg-muted/20 flex flex-col">
          {/* Header in Sidebar */}
          <div className="p-6 border-b">
            <h1 className="text-2xl font-bold tracking-tight">Documentation</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Platform structure guide
            </p>
          </div>

          {/* Vertical Tabs Navigation */}
          <TabsList className="flex-col h-auto w-full bg-transparent p-3 gap-1 items-stretch justify-start">
            {visibleTabs.map((tab) => (
              <div key={tab.id}>
                {/* Parent Tab */}
                <div className="relative">
                  <TabsTrigger
                    value={tab.id}
                    onClick={(e) => {
                      // If tab has children, toggle expand/collapse
                      if (tab.children) {
                        e.preventDefault();
                        toggleExpand(tab.id);
                        // Also navigate to the parent tab
                        handleTabChange(tab.id);
                      }
                    }}
                    className="justify-start gap-3 px-4 py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm w-full"
                  >
                    <tab.icon className="h-4 w-4 shrink-0" />
                    <span className="text-left flex-1">{tab.label}</span>
                    {tab.children && (
                      <ChevronRight
                        className={cn(
                          "h-3 w-3 transition-transform shrink-0",
                          expandedTabs.includes(tab.id) && "rotate-90"
                        )}
                      />
                    )}
                  </TabsTrigger>
                </div>

                {/* Child Tabs */}
                {tab.children && expandedTabs.includes(tab.id) && (
                  <div className="ml-4 mt-1 space-y-1">
                    {tab.children.map((child) => (
                      <TabsTrigger
                        key={child.id}
                        value={child.id}
                        className="justify-start gap-3 px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm w-full text-sm"
                      >
                        <child.icon className="h-3 w-3 shrink-0" />
                        <span className="text-left">{child.label}</span>
                      </TabsTrigger>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </TabsList>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 max-w-6xl">
            <TabsContent value="overview" className="mt-0">
              <OverviewTab />
            </TabsContent>

            <TabsContent value="video-demos" className="mt-0">
              <VideoDemosTab />
            </TabsContent>

            {/* Use Cases - Parent shows E2E flows, children show specific content */}
            <TabsContent value="use-cases" className="mt-0">
              <E2EFlowsContent />
            </TabsContent>
            <TabsContent value="use-cases.e2e-flows" className="mt-0">
              <E2EFlowsContent />
            </TabsContent>
            <TabsContent value="use-cases.b2b" className="mt-0">
              <B2BContent />
            </TabsContent>
            <TabsContent value="use-cases.b2c" className="mt-0">
              <B2CContent />
            </TabsContent>
            <TabsContent value="use-cases.comparison" className="mt-0">
              <ComparisonContent />
            </TabsContent>

            {/* Compliance - Parent and children */}
            <TabsContent value="compliance" className="mt-0">
              <ComplianceOverviewContent />
            </TabsContent>
            <TabsContent value="compliance.overview" className="mt-0">
              <ComplianceOverviewContent />
            </TabsContent>
            <TabsContent value="compliance.executive" className="mt-0">
              <ComplianceDocContent
                title="Executive Summary"
                description="For CEO, CFO, Project Leads"
                icon={Users}
                filePath="/docs/COMPLIANCE_EXECUTIVE_SUMMARY.md"
              />
            </TabsContent>
            <TabsContent value="compliance.roadmap" className="mt-0">
              <ComplianceDocContent
                title="Roadmap"
                description="For Project Managers"
                icon={TrendingUp}
                filePath="/docs/COMPLIANCE_ROADMAP.md"
              />
            </TabsContent>
            <TabsContent value="compliance.technical" className="mt-0">
              <ComplianceDocContent
                title="Technical Architecture"
                description="For Architects, Engineers"
                icon={Building2}
                filePath="/docs/COMPLIANCE_TECHNICAL_ARCHITECTURE.md"
              />
            </TabsContent>
            <TabsContent value="compliance.legal" className="mt-0">
              <ComplianceDocContent
                title="Legal Summary"
                description="For Legal, Compliance Officers"
                icon={Shield}
                filePath="/docs/COMPLIANCE_LEGAL_SUMMARY.md"
              />
            </TabsContent>

            <TabsContent value="organizations" className="mt-0">
              <OrganizationsTab />
            </TabsContent>

            <TabsContent value="business-units" className="mt-0">
              <BusinessUnitsTab />
            </TabsContent>

            <TabsContent value="products" className="mt-0">
              <ProductsTab />
            </TabsContent>

            <TabsContent value="customers" className="mt-0">
              <CustomersTab />
            </TabsContent>

            <TabsContent value="wallets" className="mt-0">
              <WalletsTab />
            </TabsContent>

            <TabsContent value="cards" className="mt-0">
              <CardsTab />
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
}
